import React, { ReactElement, useState } from "react";
import styled from "styled-components";

import { formatWithLocale as formatDate, parseDate } from "src/gantt/helpers/DateHelpers";

import "flatpickr/dist/themes/airbnb.css";
import { Finnish } from 'flatpickr/dist/l10n/fi';

import Flatpickr from "react-flatpickr";
import { iRootState } from "src/rematch/store";
import { connect } from "react-redux";

interface IEditStringInputProps {
  hasError: boolean;
  hasChanged: boolean;
}

const EditDateInput = styled.input<IEditStringInputProps>`
  background: ${props => props.hasError ? '#ffc0c0' : props.hasChanged ? '#ffffc0' : 'white' };
  font-size: 12px;
  font-family: Segoe UI;
  height: 30px;
  width: 100%;
  padding-left: 10px;
  border-radius: 4px;
  border: 1px solid hsl(0,0%,80%);
`;

/** Render a custom date input wrapper with styles */
const CustomDateInput = ({ defaultValue, inputRef, ...props }) => {
  return <EditDateInput {...props} defaultValue={defaultValue} ref={inputRef} hasChange={props.hasChange} hasError={props.hasError} />;
};

interface EditDateBaseProps {
  dateFormat    : string,
  propertyValue : string;
  enableTime    : boolean;
  hasError      : boolean;
  hasChanged    : boolean;
  onChange      : (value : string) => void;

  // from connect
  globalDateFormat? : string;
  globalTimeFormat? : string;
  showTimeInTable?  : boolean;
  language?         : string;
}

const getDateFormat = (dateFnsFormat: string, language: string): string => {

  switch ( dateFnsFormat ) {

    case "P":

      if ( language === "fi" ) {
        return "j.n.Y"
      } else {
        return "d/m/Y"
      }

    case "PP":

      if ( language === "fi" ) {
        return "d. M Y"
      } else {
        return "j M Y"
      }

    case "yyyy-MM-dd":
      return "Y-m-d"

    case "dd.MM.yyyy":
      return "d.m.Y"

    case "dd/MM/yyyy":
      return "d/m/Y"

    case "MM/dd/yyyy":
      return "m/d/Y"

    default:
      return dateFnsFormat
  }
}

/**
 * This is a hack to conver "P", "PP" and "p" formatting to Flatpickr supported formats
 * @param props 
 * @returns 
 */
const convertToFlatpickrDateFormat = (props: EditDateBaseProps, language: string): string => {

  let flatpickrDateFromat = getDateFormat( props.globalDateFormat, language );

  if ( props.showTimeInTable ) {

    let timeFormat = "";

    switch ( props.globalTimeFormat ) {

      case "p":
        timeFormat = language === "fi" ? "H.i" : 'H:i';
        break;

      case "HH:mm" :
        timeFormat = "H:i";
        break;

      case "HH.mm" :
        timeFormat = "H.i";
        break;

      case "h.mm a" :
        timeFormat = "h.i K";
        break;
    }

    flatpickrDateFromat = flatpickrDateFromat + " " + timeFormat;
  }

  return flatpickrDateFromat;
}



const EditDateProperty = (props: EditDateBaseProps): ReactElement => {

  const [stringValue, setStringValue] = useState(props.propertyValue);

  const language = props?.language === "fi" ? Finnish : "en";

  const flatpickrDateFromat = convertToFlatpickrDateFormat( props, props?.language );
  const defaultValue       = stringValue !== '' ? parseDate(stringValue, props.dateFormat) : '';

  const onChange = (date: Date) => {
    const dateString = date ? formatDate(date, props.dateFormat) : '';

    setStringValue( dateString );
    props.onChange( dateString ); // this should be string
  };

  
  return (
    <Flatpickr
      data-enable-time={ props.enableTime }
      value={ defaultValue }
      options={{
        dateFormat: flatpickrDateFromat,
        time_24hr: true,
        locale: language
      }}
      onChange={ ([date]) => onChange(date) }
      render={
        ({defaultValue, value, ...props}, ref) => {
          return (
            <CustomDateInput 
              {...props} 
              value={value} 
              defaultValue={defaultValue} 
              inputRef={ref} 
              hasChanged={props.hasChanged}
              hasError={props.hasError}
            />
          )
        }
      }
    />
  );
};

export default connect(
  (state: iRootState) => ({
    globalDateFormat: state.UI.displaySettings.dateFormat,
    globalTimeFormat: state.UI.displaySettings.timeFormat,
    showTimeInTable: state.UI.displaySettings.showTimeInTable,
    language: state.common.language
  }),
)(EditDateProperty);
